import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Player, CountdownGameState, CountdownPlayer } from '../../../shared/types/game';
import { UI_TEXT_KEYS, CSS_CLASSES } from '../../../shared/utils/i18nConstants';
import { soundManager } from '../../../utils/audio/soundManager';
import { useScoreSubmission } from '../../player/hooks/useScoreSubmission';
import { formatCountdownModeIndicator } from '../../../shared/utils/textUtils';
import CountdownPlayerHistoryModal from '../../history/components/CountdownPlayerHistoryModal';
import CountdownAllPlayersHistoryModal from '../../history/components/CountdownAllPlayersHistoryModal';
import PlayerList from '../../player/components/PlayerList';
import GameActions from './GameActions';
import WinnerAnnouncement from './WinnerAnnouncement';
import ScoreInput from '../../player/components/ScoreInput';
import './GameBoard.css';

interface CountdownGameBoardProps {
  gameState: CountdownGameState;
  currentPlayer: CountdownPlayer | null;
  onSubmitScore: (playerId: string, score: number) => void;
  onNextPlayer: () => void;
  onResetGame: () => void;
  onNewGame: () => void;
  onUndoLastMove: () => void;
  canUndo: boolean;
}

const CountdownGameBoard: React.FC<CountdownGameBoardProps> = React.memo(({
  gameState,
  currentPlayer,
  onSubmitScore,
  onNextPlayer: _onNextPlayer,
  onResetGame,
  onNewGame,
  onUndoLastMove,
  canUndo,
}) => {
  const { t } = useTranslation();
  const [historyPlayer, setHistoryPlayer] = useState<Player | null>(null);
  const [showConsolidatedHistory, setShowConsolidatedHistory] = useState(false);
  const [showBustBanner, setShowBustBanner] = useState(false);
  const [bustAnimKey, setBustAnimKey] = useState(0);

  const {
    scoreInput,
    error,
    handleSubmitScore,
    handleScoreInputChange
  } = useScoreSubmission({
    onSubmitScore,
    onNextPlayer: undefined,
    gameFinished: gameState.gameFinished
  });

  // Incrementing counter that changes whenever any score entry is added.
  // This lets us re-trigger the bust banner even if the bust flag remains true.
  const historyVersion = useMemo(
    () => gameState.players.reduce((acc, p) => acc + p.scoreHistory.length, 0),
    [gameState.players]
  );

  // When a bust occurs, show the banner and auto-dismiss after a short delay,
  // but allow it to be visible at the start of the next player's turn.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (gameState.lastThrowWasBust) {
      setShowBustBanner(true);
      setBustAnimKey((k) => k + 1);
      soundManager.playSound('bust');
      timer = setTimeout(() => setShowBustBanner(false), 2000);
    } else {
      setShowBustBanner(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [gameState.lastThrowWasBust, currentPlayer?.id, historyVersion]);

  const handleScoreSubmit = useCallback(() => {
    if (currentPlayer) {
      handleSubmitScore(currentPlayer.id);
    }
  }, [currentPlayer, handleSubmitScore]);

  const handleScoreSubmitWithValue = useCallback((score: number) => {
    if (currentPlayer) {
      onSubmitScore(currentPlayer.id, score);
    }
  }, [currentPlayer, onSubmitScore]);

  const handleBustSubmit = useCallback(() => {
    if (currentPlayer) {
      onSubmitScore(currentPlayer.id, currentPlayer.score + 1);
    }
  }, [currentPlayer, onSubmitScore]);

  if (gameState.gameFinished && gameState.winner) {
    return (
      <>
        <WinnerAnnouncement
          winner={gameState.winner}
          players={gameState.players}
          gameMode="countdown"
          onHistoryClick={setHistoryPlayer}
          onShowAllHistory={() => setShowConsolidatedHistory(true)}
          onResetGame={onResetGame}
          onNewGame={onNewGame}
        />
        
        <CountdownPlayerHistoryModal 
          player={historyPlayer! as CountdownPlayer}
          isOpen={historyPlayer !== null}
          onClose={() => setHistoryPlayer(null)}
        />
        
        <CountdownAllPlayersHistoryModal 
          players={gameState.players as CountdownPlayer[]}
          isOpen={showConsolidatedHistory}
          onClose={() => setShowConsolidatedHistory(false)}
          onUndoLastMove={onUndoLastMove}
          canUndo={canUndo}
        />
      </>
    );
  }

  return (
    <>
      <div className={CSS_CLASSES.GAME_BOARD}>
        <h1>{t(UI_TEXT_KEYS.APP_TITLE)}</h1>
        <div className={CSS_CLASSES.GAME_MODE_INDICATOR}>
          {formatCountdownModeIndicator(gameState.startingScore)}
        </div>
        
        <PlayerList
          players={gameState.players}
          currentPlayer={currentPlayer}
          gameMode="countdown"
          onHistoryClick={setHistoryPlayer}
        />

        {showBustBanner && (
          <div className={CSS_CLASSES.BUST_BANNER} key={bustAnimKey}>
            {t(UI_TEXT_KEYS.BUST_MESSAGE)}
          </div>
        )}

        {currentPlayer && !gameState.gameFinished && (
          <ScoreInput
            currentPlayer={currentPlayer}
            scoreInput={scoreInput}
            onScoreInputChange={handleScoreInputChange}
            onSubmitScore={handleScoreSubmit}
            onSubmitScoreWithValue={handleScoreSubmitWithValue}
            onSubmitBust={handleBustSubmit}
            error={error}
          />
        )}
        
        <GameActions
          onShowAllHistory={() => setShowConsolidatedHistory(true)}
          onResetGame={onResetGame}
          onNewGame={onNewGame}
        />
      </div>

      {/* History modals - always rendered so they're available during the game */}
      <CountdownPlayerHistoryModal 
        player={historyPlayer! as CountdownPlayer}
        isOpen={historyPlayer !== null}
        onClose={() => setHistoryPlayer(null)}
      />
      
      <CountdownAllPlayersHistoryModal 
        players={gameState.players as CountdownPlayer[]}
        isOpen={showConsolidatedHistory}
        onClose={() => setShowConsolidatedHistory(false)}
        onUndoLastMove={onUndoLastMove}
        canUndo={canUndo}
      />
    </>
  );
});

CountdownGameBoard.displayName = 'CountdownGameBoard';

export default CountdownGameBoard;
